// Checklist task types with photo requirement support and bilingual fields

/** Checklist-level type. Tasks inside have no per-task type. */
export type ChecklistType = "cleaning" | "maintenance";

export const CHECKLIST_TYPE_LABELS: Record<ChecklistType, string> = {
  cleaning: "Cleaning",
  maintenance: "Maintenance",
};

import type { GuestAmenityPlacement, LeaveOutRule } from "@/lib/leaveOut";
import type { LeaveOutPlan } from "@/lib/leaveOutPlan";

/** Optional bundle row under a guest amenity staging line (e.g. gift basket parts). */
export interface GuestAmenityStagingSubitem {
  id: string;
  item_label: string;
  /** Optional link to an existing inventory row (reference only; does not create inventory). */
  inventory_item_id?: string | null;
  description?: string;
  target_count: number;
}

/**
 * Checklist-defined guest amenity staging: what to leave out, where, counts, host reference photo.
 * Stored on `checklists.guest_amenity_staging` (not `inventory_items`).
 * Slice 44: `leave_out_rule` + `placements` — cleaners never see the rule math.
 */
export interface GuestAmenityStagingLine {
  id: string;
  /** `inventory_rooms.id` for the placement room (legacy single-room fallback). */
  property_room_id: string | null;
  /** Denormalized room label for cleaners if the room is renamed later. */
  room_name_snapshot?: string | null;
  /** Optional link to an existing inventory row when picked from search. */
  inventory_item_id: string | null;
  item_label: string;
  description: string;
  /**
   * Legacy / Fixed leave-out count. Also mirrors `leave_out_rule.qty` for older readers.
   */
  target_count: number;
  /** Slice 44 — Fixed or Per guests. Absent → treat as Fixed using `target_count`. */
  leave_out_rule?: LeaveOutRule | null;
  /** Slice 46 — places + one row per party size. Wins over `leave_out_rule` when set. */
  leave_out_plan?: LeaveOutPlan | null;
  /**
   * Slice 44 — where to put items (weights). Cleaner sees "1 in Bathroom 1 · 1 in Bathroom 2".
   * Empty → fall back to `room_name_snapshot` as a single place.
   */
  placements?: GuestAmenityPlacement[];
  host_reference_photo_url?: string | null;
  subitems?: GuestAmenityStagingSubitem[];
}

/** @deprecated Per-task type; only used for legacy data. New checklists use ChecklistType on the checklist. */
export type TaskType = "cleaning" | "maintenance" | "quick_check";

export interface CleaningSettings {
  require_reference_photo?: boolean;
  require_before_photo?: boolean;
  require_after_photo?: boolean;
}

export interface MaintenanceSettings {
  question?: string;
  require_photos?: boolean;
  allow_notes?: boolean;
}

export interface QuickCheckSettings {
  require_photo?: boolean;
}

// Legacy photo requirements interface - kept for backward compatibility
export interface TaskPhotoRequirements {
  require_reference_photo?: boolean;
  require_before_photo?: boolean;
  require_after_photo?: boolean;
}

/** Cleaning category for filtering/grouping. Only used when Task Type = Cleaning. "auto" = derive from task text. Kitchen and bathroom removed as subcategories. */
export type CleaningCategory =
  | "auto"
  | "floors"
  | "surfaces"
  | "beds_linens"
  | "laundry"
  | "trash_recycling"
  | "restock"
  | "guest_amenities"
  | "dusting"
  | "glass_mirrors"
  | "reset_staging";

export const CLEANING_CATEGORY_LABELS: Record<CleaningCategory, string> = {
  auto: "Auto",
  floors: "Floors",
  surfaces: "Surfaces",
  beds_linens: "Beds & Linens",
  laundry: "Laundry",
  trash_recycling: "Trash & Recycling",
  restock: "Restock",
  guest_amenities: "Guest amenities (leave out)",
  dusting: "Dusting",
  glass_mirrors: "Glass & Mirrors",
  reset_staging: "Reset & Staging",
};

/** @deprecated Use CleaningCategory and CLEANING_CATEGORY_LABELS. Kept for backward compatibility. */
export type TaskSubcategory = CleaningCategory;
/** @deprecated Use CLEANING_CATEGORY_LABELS. */
export const TASK_SUBCATEGORY_LABELS = CLEANING_CATEGORY_LABELS;

export interface TaskObject extends TaskPhotoRequirements {
  text: string;
  // Bilingual fields
  text_en?: string;
  text_es?: string;
  /** @deprecated No longer set on new tasks; checklist type defines context. Kept for legacy JSON. */
  task_type?: TaskType;
  cleaning_settings?: CleaningSettings;
  maintenance_settings?: MaintenanceSettings;
  quick_check_settings?: QuickCheckSettings;
  /** Cleaning category (only for cleaning tasks). Default "auto" = infer from text. */
  subcategory?: CleaningCategory | string;
  /**
   * For `guest_amenities` tasks: how many of this item to leave out for each turnover (shown on cleaner checklist).
   */
  guest_restock_quantity?: number | null;
}

// Tasks can be either a string (legacy) or an object with requirements
export type Task = string | TaskObject;

export interface Room {
  name: string;
  // Bilingual fields for room name
  name_en?: string;
  name_es?: string;
  tasks: Task[];
  /** Code to enter this room (e.g. lockbox, closet code). Shown to cleaner. */
  entry_code?: string | null;
  /** Notes for entering this room (e.g. "Key under mat"). Shown to cleaner. */
  entry_notes?: string | null;
  /** @deprecated Prefer property_room_id. Link to inventory_rooms for backward compat / cleaner inventory. */
  inventory_room_id?: string | null;
  /** Canonical room (single source of truth). Checklist and Inventory both reference this. */
  property_room_id?: string | null;
}

// Helper to normalize a task to always get the text
export const getTaskText = (task: Task): string => {
  return typeof task === 'string' ? task : task.text;
};

// Helper to get localized task text based on language
export const getLocalizedTaskText = (task: Task, lang: string = 'en'): string => {
  if (typeof task === 'string') return task;
  if (lang === 'es' && task.text_es) return task.text_es;
  if (lang === 'en' && task.text_en) return task.text_en;
  return task.text;
};

// Helper to get localized room name based on language
export const getLocalizedRoomName = (room: Room, lang: string = 'en'): string => {
  if (lang === 'es' && room.name_es) return room.name_es;
  if (lang === 'en' && room.name_en) return room.name_en;
  return room.name;
};

// Default settings
export const DEFAULT_CLEANING_SETTINGS: CleaningSettings = {
  require_reference_photo: false,
  require_before_photo: false,
  require_after_photo: false,
};

export const DEFAULT_MAINTENANCE_SETTINGS: MaintenanceSettings = {
  question: '',
  require_photos: false,
  allow_notes: true,
};

export const DEFAULT_QUICK_CHECK_SETTINGS: QuickCheckSettings = {
  require_photo: false,
};

// Helper to get the task type
export const getTaskType = (task: Task): TaskType => {
  if (typeof task === 'string') return 'cleaning';
  return task.task_type ?? 'cleaning';
};

/** Infer cleaning category from task text when category is "auto" or missing. Kitchen/bathroom keywords map to surfaces. */
function inferCleaningCategoryFromText(text: string): CleaningCategory {
  const lower = (text || "").trim().toLowerCase();
  if (/mop|vacuum|sweep|floor|floors/.test(lower)) return "floors";
  if (/bed|linen|sheet|towel|pillow/.test(lower)) return "beds_linens";
  if (/laundry|wash|dry/.test(lower)) return "laundry";
  if (/trash|recycl|garbage/.test(lower)) return "trash_recycling";
  if (
    /toilet paper|tp\b|paper towel|coffee pod|k-cup|nespresso|shampoo|conditioner|body wash|soap bar|hand soap|lotion|snack|water bottle|sparkling water|welcome|amenit|minibar|mini-bar|leave out|stock the|guest amenit/.test(
      lower
    )
  ) {
    return "guest_amenities";
  }
  if (/restock|replenish/.test(lower)) return "restock";
  if (/dust/.test(lower)) return "dusting";
  if (/glass|mirror/.test(lower)) return "glass_mirrors";
  if (/reset|staging/.test(lower)) return "reset_staging";
  if (/wipe|surface|surfaces|clean counter|bathroom|toilet|shower|tub|vanity|kitchen|stove|fridge|dish|sink/.test(lower)) return "surfaces";
  return "surfaces"; // default
}

/** Resolved cleaning category: uses stored value or infers when "auto". Legacy bathroom/kitchen map to surfaces. */
export const getTaskCleaningCategory = (task: Task): CleaningCategory => {
  const text = getTaskText(task);
  if (typeof task === "string") return inferCleaningCategoryFromText(text);
  const raw = task.subcategory;
  if (raw && raw !== "auto") {
    if (raw === "bathroom" || raw === "kitchen") return "surfaces"; // legacy subcategories removed
    if (Object.keys(CLEANING_CATEGORY_LABELS).includes(raw)) return raw as CleaningCategory;
  }
  return inferCleaningCategoryFromText(text);
};

/** Quantity to leave out per turnover for guest-amenities tasks; null if unset or invalid. */
export function getGuestRestockQuantity(task: Task): number | null {
  if (typeof task === "string") return null;
  const raw = (task as TaskObject).guest_restock_quantity;
  if (raw == null || !Number.isFinite(Number(raw))) return null;
  const n = Math.floor(Number(raw));
  if (n < 1) return null;
  return Math.min(999, n);
}

/** @deprecated Use getTaskCleaningCategory. */
export const getTaskSubcategory = getTaskCleaningCategory;

// Helper to get cleaning settings (merges legacy fields with cleaning_settings)
export const getCleaningSettings = (task: Task): CleaningSettings => {
  if (typeof task === 'string') return { ...DEFAULT_CLEANING_SETTINGS };
  
  // If cleaning_settings exists, use it
  if (task.cleaning_settings) {
    return {
      require_reference_photo: task.cleaning_settings.require_reference_photo ?? false,
      require_before_photo: task.cleaning_settings.require_before_photo ?? false,
      require_after_photo: task.cleaning_settings.require_after_photo ?? false,
    };
  }
  
  // Fall back to legacy top-level fields
  return {
    require_reference_photo: task.require_reference_photo ?? false,
    require_before_photo: task.require_before_photo ?? false,
    require_after_photo: task.require_after_photo ?? false,
  };
};

// Helper to get maintenance settings
export const getMaintenanceSettings = (task: Task): MaintenanceSettings => {
  if (typeof task === 'string') return { ...DEFAULT_MAINTENANCE_SETTINGS };
  
  return {
    question: task.maintenance_settings?.question ?? '',
    require_photos: task.maintenance_settings?.require_photos ?? false,
    allow_notes: task.maintenance_settings?.allow_notes ?? true,
  };
};

// Helper to get quick check settings
export const getQuickCheckSettings = (task: Task): QuickCheckSettings => {
  if (typeof task === 'string') return { ...DEFAULT_QUICK_CHECK_SETTINGS };
  
  return {
    require_photo: task.quick_check_settings?.require_photo ?? false,
  };
};

// Helper to get a task as an object with all properties
export const normalizeTask = (task: Task): TaskObject => {
  if (typeof task === 'string') {
    return {
      text: task,
      task_type: 'cleaning',
      require_reference_photo: false,
      require_before_photo: false,
      require_after_photo: false,
      cleaning_settings: { ...DEFAULT_CLEANING_SETTINGS },
      maintenance_settings: { ...DEFAULT_MAINTENANCE_SETTINGS },
      quick_check_settings: { ...DEFAULT_QUICK_CHECK_SETTINGS },
    };
  }
  
  // Safety check for invalid task objects
  if (!task || typeof task !== 'object' || !task.text) {
    return {
      text: '',
      task_type: 'cleaning',
      require_reference_photo: false,
      require_before_photo: false,
      require_after_photo: false,
      cleaning_settings: { ...DEFAULT_CLEANING_SETTINGS },
      maintenance_settings: { ...DEFAULT_MAINTENANCE_SETTINGS },
      quick_check_settings: { ...DEFAULT_QUICK_CHECK_SETTINGS },
    };
  }
  
  const taskType = task.task_type ?? 'cleaning';
  const cleaningSettings = getCleaningSettings(task);
  const maintenanceSettings = getMaintenanceSettings(task);
  const quickCheckSettings = getQuickCheckSettings(task);
  
  return {
    text: task.text || '',
    text_en: task.text_en,
    text_es: task.text_es,
    task_type: taskType,
    // Keep legacy fields for backward compatibility
    require_reference_photo: cleaningSettings.require_reference_photo,
    require_before_photo: cleaningSettings.require_before_photo,
    require_after_photo: cleaningSettings.require_after_photo,
    // New structured settings
    cleaning_settings: cleaningSettings,
    maintenance_settings: maintenanceSettings,
    quick_check_settings: quickCheckSettings,
    subcategory: (task as TaskObject).subcategory,
    guest_restock_quantity: (task as TaskObject).guest_restock_quantity ?? null,
  };
};

// Helper to check if any photo is required for a task
export const hasPhotoRequirement = (task: Task): boolean => {
  if (typeof task === 'string') return false;
  
  const taskType = getTaskType(task);
  
  if (taskType === 'cleaning') {
    const settings = getCleaningSettings(task);
    return !!(settings.require_reference_photo || settings.require_before_photo || settings.require_after_photo);
  }
  
  if (taskType === 'maintenance') {
    const settings = getMaintenanceSettings(task);
    return !!settings.require_photos;
  }
  
  if (taskType === 'quick_check') {
    const settings = getQuickCheckSettings(task);
    return !!settings.require_photo;
  }
  
  return false;
};

// Helper to check if task is a maintenance task
export const isMaintenanceTask = (task: Task): boolean => {
  return getTaskType(task) === 'maintenance';
};

// Helper to check if task is a cleaning task
export const isCleaningTask = (task: Task): boolean => {
  return getTaskType(task) === 'cleaning';
};

// Helper to check if task is a quick check task
export const isQuickCheckTask = (task: Task): boolean => {
  return getTaskType(task) === 'quick_check';
};
